import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import AdminHeader from '../../../components/ui/AdminHeader';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

import { getFinancialSummary, getRevenueStats, exportOrdersCSV, getDashboardStats } from '../../../services/adminService';

const AdminFinancialPage = () => {
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start_date: '',
    end_date: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  // Check if user is admin
  useEffect(() => {
    if (userProfile && userProfile?.role !== 'admin') {
      navigate('/');
      return;
    }
  }, [userProfile, navigate]);

  const loadFinancialData = async () => {
    try {
      setLoading(true);
      const [ordersData, dashboardStats, revenueStats] = await Promise.all([
        getFinancialSummary(dateRange),
        getDashboardStats(),
        getRevenueStats('month')
      ]);
      
      setOrders(ordersData || []);
      setStats(dashboardStats);
      setRevenueData(revenueStats || []);
    } catch (error) {
      setError(`Failed to load financial data: ${error?.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFinancialData();
  }, [dateRange]);

  const handleExportCSV = async () => {
    try {
      setError('');
      const csvData = await exportOrdersCSV(dateRange);
      
      // Create CSV content
      const csvContent = [
        csvData?.headers?.join(','),
        ...csvData?.data?.map(row => row?.join(','))
      ]?.join('\n');
      
      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL?.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `orders-export-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      window.URL?.revokeObjectURL(url);
      
      setSuccess('Orders exported successfully');
    } catch (error) {
      setError(`Failed to export orders: ${error?.message}`);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    })?.format(amount || 0);
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      succeeded: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
      refunded: 'bg-blue-100 text-blue-800'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  if (userProfile?.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader
        title="Financial Reports"
        description="View platform revenue, analytics, and financial data"
        user={{
          name: userProfile?.full_name || "Admin User",
          role: "Platform Administrator"
        }}
      >
        <Button
          onClick={handleExportCSV}
          iconName="Download"
          variant="outline"
        >
          Export CSV
        </Button>
      </AdminHeader>

      <main className="p-6">
        <div className="max-w-7xl mx-auto">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}
          
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
              {success}
            </div>
          )}

          {/* Section Header */}
          <div className="mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="DollarSign" size={20} color="white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">Financial Reports</h2>
                <p className="text-sm text-muted-foreground">View platform revenue, analytics, and financial data</p>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Total Revenue</p>
                    <p className="text-2xl font-bold text-foreground">
                      {formatCurrency(stats?.revenue?.total)}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Icon name="DollarSign" size={24} color="#16a34a" />
                  </div>
                </div>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Total Orders</p>
                    <p className="text-2xl font-bold text-foreground">{stats?.orders?.total}</p>
                    <p className="text-xs text-muted-foreground">
                      {stats?.orders?.confirmed} confirmed
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Icon name="ShoppingCart" size={24} color="#2563eb" />
                  </div>
                </div>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Total Events</p>
                    <p className="text-2xl font-bold text-foreground">{stats?.events?.total}</p>
                    <p className="text-xs text-muted-foreground">
                      {stats?.events?.published} published
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Icon name="Calendar" size={24} color="#7c3aed" />
                  </div>
                </div>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Total Users</p>
                    <p className="text-2xl font-bold text-foreground">{stats?.users?.total}</p>
                    <p className="text-xs text-muted-foreground">
                      {stats?.users?.consumers} consumers
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Icon name="Users" size={24} color="#ea580c" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Date Range Filters */}
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Start Date"
                type="date"
                value={dateRange?.start_date}
                onChange={(e) => setDateRange({...dateRange, start_date: e?.target?.value})}
              />
              <Input
                label="End Date"
                type="date"
                value={dateRange?.end_date}
                onChange={(e) => setDateRange({...dateRange, end_date: e?.target?.value})}
              />
              <div className="text-sm text-muted-foreground flex items-end pb-2">
                Showing {orders?.length || 0} orders
              </div>
            </div>
          </div>

          {/* Orders Table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">Recent Orders</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Order</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Customer</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Event</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Amount</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Payment Status</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="text-center py-8 text-muted-foreground">
                        Loading orders...
                      </td>
                    </tr>
                  ) : orders?.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-8 text-muted-foreground">
                        No orders found
                      </td>
                    </tr>
                  ) : (
                    orders?.slice(0, 50)?.map((order) => (
                      <tr key={order?.id} className="border-t border-border hover:bg-muted/50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-foreground">{order?.order_number}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <div className="font-medium text-foreground">{order?.user?.full_name || 'Guest'}</div>
                            <div className="text-sm text-muted-foreground">{order?.customer_email}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-foreground">{order?.event?.title || 'N/A'}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-foreground">
                            {formatCurrency(order?.total_amount)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(order?.payment_status)}`}>
                            {order?.payment_status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {new Date(order?.created_at)?.toLocaleDateString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminFinancialPage;